import { FC, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { useMount } from 'react-use';
import { Stack } from '@mui/material';
import { snakeCase } from 'lodash';

import { stationStatusMessages } from './constants';

import { LoadingScreen, MainButton } from '~components/atoms';
import { LocalizationTypography } from '~components/organisms';
import { HOMEPAGE_PATH, WELCOME_BACK_PAGE_PATH } from '~configs';
import { useNavigateInApp } from '~hooks';
import { useStationInformationContext } from '~providers';
import { ConnectionState, StationStatus } from '~types/station.type';
import { handleOpenApp, handleOpenLinkInNewTab } from '~utils';

export const BadStatusStationPage: FC = () => {
  const { status } = useParams();
  const { connectionState, isFetchingStationInformation } =
    useStationInformationContext();
  const navigate = useNavigateInApp();

  const stationStatus = snakeCase(status).toUpperCase() as StationStatus;
  // Convert `status` to a valid `StationStatus`
  const validStationStatus = Object.values(StationStatus).includes(
    stationStatus,
  )
    ? stationStatus
    : null;

  const isOfflineStatus = validStationStatus === StationStatus.OFFLINE;
  const isAlreadyRented = validStationStatus === StationStatus.ALREADY_RENTED;
  const isIssueEjecting = validStationStatus === StationStatus.ISSUE_EJECTING;

  /**
   * Navigate effect
   * Redirect on mount if the status is invalid or the FFg is off
   */
  useMount(() => {
    if (!validStationStatus) {
      navigate('/error');
    }
  });

  /**
   * Navigate effect
   * Redirect to homepage if station is back online (connectionState is not NO_INTERNET)
   */
  useEffect(() => {
    if (
      isOfflineStatus &&
      connectionState &&
      connectionState !== ConnectionState.NO_INTERNET
    ) {
      navigate(HOMEPAGE_PATH);
    }
  }, [connectionState, isOfflineStatus, navigate]);

  const guidingText = useMemo(() => {
    const props = {
      textAlign: 'center',
      variant: 'Inter/Body-md',
      color: '#6B7280',
    } as const;
    if (isAlreadyRented)
      return (
        <LocalizationTypography
          {...props}
          textId="app.badStatusStationPage.text.guidingAlreadyRented"
          defaultText="Please return your current charger to rent another one. Need more help? Text support for assistance."
        />
      );

    if (isIssueEjecting)
      return (
        <LocalizationTypography
          {...props}
          textId="app.badStatusStationPage.text.guidingIssueEjecting"
          defaultText="The charger didn’t eject properly. You haven’t been charged. Try again and we’ll release a different one."
        />
      );

    return (
      <LocalizationTypography
        {...props}
        textId="app.badStatusStationPage.text.guiding"
        defaultText="Please check back later or find nearby stations in our app. Need more
          help? Text support for assistance."
      />
    );
  }, [isAlreadyRented, isIssueEjecting]);

  const findNearbyStationBtn = useMemo(() => {
    if (isAlreadyRented) return null;

    if (isIssueEjecting)
      return (
        <MainButton
          variant="contained"
          color="primary"
          onClick={() => navigate(WELCOME_BACK_PAGE_PATH)}
        >
          <LocalizationTypography
            variant="Poppins/Subheading-md"
            textId="app.badStatusStationPage.button.ejectAnotherCharger"
            defaultText="Eject Another Charger"
          />
        </MainButton>
      );

    return (
      <MainButton variant="contained" color="primary" onClick={handleOpenApp}>
        <LocalizationTypography
          variant="Poppins/Subheading-md"
          textId="app.badStatusStationPage.button.findStationsInApp"
          defaultText="Find Nearby Stations in App"
        />
      </MainButton>
    );
  }, [isAlreadyRented, isIssueEjecting, navigate]);

  if (!validStationStatus || isFetchingStationInformation) {
    return <LoadingScreen />;
  }

  const stationStatusMessage = stationStatusMessages[validStationStatus];
  const StationStatusImage = stationStatusMessage.img;

  return (
    <Stack
      sx={{
        gap: 4,
        backgroundColor: 'white', // Set background color to white
        width: '100%',
        p: 2.5,
      }}
    >
      <Stack
        gap={1}
        sx={{
          alignItems: 'center',
        }}
      >
        {StationStatusImage && <StationStatusImage />}

        <LocalizationTypography
          sx={{ mt: 1.5 }}
          textId={stationStatusMessage.localeTextId}
          defaultText={stationStatusMessage.title}
          variant="Poppins/Heading-3"
          textAlign="center"
        />

        {/* GUIDING TEXT */}
        {guidingText}
      </Stack>

      {/* BUTTONS */}
      <Stack sx={{ gap: 2 }}>
        {/* BUTTON USE APP */}
        {findNearbyStationBtn}

        {/* BUTTON TEXT USER SUPPORT */}
        <MainButton
          variant="outlined"
          color="primary"
          onClick={handleOpenLinkInNewTab('sms:+1 (213) 928-7320')}
        >
          <LocalizationTypography
            variant="Poppins/Subheading-md"
            textId="app.badStatusStationPage.button.textUserSupport"
            defaultText="Text Support"
          />
        </MainButton>
      </Stack>
    </Stack>
  );
};
